import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
	useGenerateSmartProgram,
	usePollGenerationStatus,
} from "../../../hooks/useTreatmentPlans";
import { GenerateSmartProgramResponse } from "../../../services/treatmentPlan.service";
import SmartProgramPreview from "./SmartProgramPreview";

interface SmartProgramConfigProps {
	treatmentPlanId: string;
	onApprove: (result: GenerateSmartProgramResponse) => void;
	onCancel: () => void;
}

const SmartProgramConfig: React.FC<SmartProgramConfigProps> = ({
	treatmentPlanId,
	onApprove,
	onCancel,
}) => {
	const [description, setDescription] = useState("");
	const [preferredMode, setPreferredMode] = useState<string | null>(null);
	const [generatedResult, setGeneratedResult] =
		useState<GenerateSmartProgramResponse | null>(null);
	const [isApproving, setIsApproving] = useState(false);
	const [isPolling, setIsPolling] = useState(false);

	const generateMutation = useGenerateSmartProgram();
	const pollQuery = usePollGenerationStatus(treatmentPlanId, isPolling);

	// Watch polling result
	useEffect(() => {
		if (!pollQuery.data) return;

		const { status, result } = pollQuery.data;

		if (status === "SUCCESS" && result) {
			setIsPolling(false);
			setGeneratedResult(result);
		} else if (status === "FAILED") {
			setIsPolling(false);
			generateMutation.reset();
		}
	}, [pollQuery.data]);

	const handleGenerate = () => {
		if (!description.trim() || description.trim().length < 10) return;

		generateMutation.mutate(
			{
				id: treatmentPlanId,
				payload: {
					description: description.trim(),
					preferredMode: preferredMode || undefined,
				},
			},
			{
				onSuccess: () => {
					// POST returned 202 — start polling
					setIsPolling(true);
				},
			},
		);
	};

	const handleApprove = async () => {
		if (!generatedResult) return;
		setIsApproving(true);
		try {
			onApprove(generatedResult);
		} finally {
			setIsApproving(false);
		}
	};

	const handleRegenerate = () => {
		setGeneratedResult(null);
		handleGenerate();
	};

	const isGenerating = generateMutation.isPending || isPolling;
	const pollStatus = pollQuery.data?.status;
	const pollError = pollQuery.data?.error;

	if (generatedResult) {
		return (
			<SmartProgramPreview
				previewHtml={generatedResult.previewHtml}
				onApprove={handleApprove}
				onRegenerate={handleRegenerate}
				onCancel={() => {
					setGeneratedResult(null);
				}}
				isApproving={isApproving}
			/>
		);
	}

	return (
		<Box>
			<Typography variant="subtitle1" sx={{ mb: 1 }}>
				Describe Your Program
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Describe the treatment program workflow in plain English. The AI will
				generate a structured configuration based on your description and
				available events.
			</Typography>

			<TextField
				fullWidth
				multiline
				rows={4}
				value={description}
				onChange={(e) => setDescription(e.target.value)}
				placeholder="Example: A 90-day substance use treatment program with daily urine tests in the first 30 days, tapering to 3 times per week. Include weekly counseling sessions and daily medication reminders at the patient's preferred dosage time."
				disabled={isGenerating}
				sx={{ mb: 2 }}
			/>

			<Box sx={{ mb: 2 }}>
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ mb: 1, display: "block" }}
				>
					Preferred Mode (optional)
				</Typography>
				<ToggleButtonGroup
					value={preferredMode}
					exclusive
					onChange={(_, value) => setPreferredMode(value)}
					size="small"
					disabled={isGenerating}
				>
					<ToggleButton value="LINEAR">Linear (Fixed Days)</ToggleButton>
					<ToggleButton value="REPEATING">Repeating (Cycle)</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{generateMutation.isError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{(generateMutation.error as Error)?.message ||
						"Failed to start generation. Please try again."}
				</Alert>
			)}

			{pollStatus === "FAILED" && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{pollError || "AI generation failed. Please try again."}
				</Alert>
			)}

			{isPolling && (
				<Alert severity="info" sx={{ mb: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<CircularProgress size={16} />
						<Typography variant="body2">
							{pollStatus === "IN_PROGRESS"
								? "AI is generating your program configuration..."
								: "Starting generation..."}
						</Typography>
					</Box>
				</Alert>
			)}

			<Box sx={{ display: "flex", gap: 2 }}>
				<Button
					variant="contained"
					startIcon={
						isGenerating ? (
							<CircularProgress size={20} color="inherit" />
						) : (
							<AutoAwesomeIcon />
						)
					}
					onClick={handleGenerate}
					disabled={isGenerating || description.trim().length < 10}
				>
					{isGenerating ? "Generating..." : "Generate with AI"}
				</Button>
				<Button variant="outlined" onClick={onCancel} disabled={isGenerating}>
					Cancel
				</Button>
			</Box>
		</Box>
	);
};

export default SmartProgramConfig;
